import React from 'react';
import type { PositionRiskWidgetInstanceConfig, PositionRiskData } from './types';
import { fetchPositionRisk } from './positionRiskService';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner } from '../../../design';
import { useWidgetData } from '../../../blocks/hooks';

// Sample data shown during dashboard construction (no asset assigned)
const SAMPLE_DATA: PositionRiskData = {
  uid: 'SAMPLE',
  bet: { positionMM: -10, varMM: 1.5, siPercentFloat: 2.0 },
  riskSize: { pctGMV: 4.0, pctEquity: 8.0, pctIdioVol: 9.0 },
  liquidity: { pctADTV: 0.5, pctADTVEPS: 0.1 },
};

interface PositionRiskWidgetRendererProps {
  config: PositionRiskWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  onDataChange?: (dataKey: string, newData: unknown) => void;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

// Placeholder for a missing value — no dangling unit (was the "— M" / "— GMV" bug).
const DASH = '—';

/** Format a number as currency in $MM (unit folded in); null -> em dash */
const fmtMM = (v: number | null): string => {
  if (v == null) return DASH;
  const abs = Math.abs(v);
  return v < 0 ? `($${abs}M)` : `$${abs}M`;
};

/** Format a percentage value; null -> em dash */
const fmtPct = (v: number | null): string => (v == null ? DASH : `${v}%`);

/** A single label/value row. Missing values render muted, never with a dangling unit. */
const Metric: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-neutral-500">{label}</span>
    <span className={`tabular-nums font-medium ${value === DASH ? 'text-neutral-300' : 'text-neutral-800'}`}>
      {value}
    </span>
  </div>
);

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400 mb-1">{children}</h4>
);

const PositionRiskWidgetRenderer: React.FC<PositionRiskWidgetRendererProps> = ({
  dashboardData = {},
  widgetInstanceId,
}) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;

  const { data, loading, error, refetch } = useWidgetData<PositionRiskData>(
    () => fetchPositionRisk(uid!),
    [uid],
    { enabled: !!uid, widgetInstanceId },
  );

  if (loading) {
    return <WidgetLoadingState size="sm" />;
  }

  if (error) {
    return <WidgetErrorState message={error} onRetry={refetch} />;
  }

  const d = uid ? data : SAMPLE_DATA;
  if (!d) return null;

  // Position, VaR and gross/net exposure only exist when there's an active
  // position to size against; flag that rather than showing bare dashes.
  const needsPosition =
    d.bet.positionMM == null && d.bet.varMM == null &&
    d.riskSize.pctGMV == null && d.riskSize.pctEquity == null;

  return (
    <div className={`h-full overflow-auto rounded-xl border border-neutral-200 bg-surface shadow-sm ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}
      <div className="p-3 space-y-3 text-sm">
        {/* Bet */}
        <div>
          <SectionHeader>Bet</SectionHeader>
          <div className="space-y-0.5">
            <Metric label="Position" value={fmtMM(d.bet.positionMM)} />
            <Metric label="VaR" value={fmtMM(d.bet.varMM)} />
            <Metric label="SI % Float" value={fmtPct(d.bet.siPercentFloat)} />
          </div>
        </div>

        {/* Risk Size */}
        <div>
          <SectionHeader>Risk Size</SectionHeader>
          <div className="space-y-0.5">
            <Metric label="GMV" value={fmtPct(d.riskSize.pctGMV)} />
            <Metric label="Equity" value={fmtPct(d.riskSize.pctEquity)} />
            <Metric label="IdioVol" value={fmtPct(d.riskSize.pctIdioVol)} />
          </div>
        </div>

        {/* Liquidity */}
        <div>
          <SectionHeader>Liquidity</SectionHeader>
          <div className="space-y-0.5">
            <Metric label="ADTV" value={fmtPct(d.liquidity.pctADTV)} />
            <Metric label="ADTV ±3-day EPS" value={fmtPct(d.liquidity.pctADTVEPS)} />
          </div>
        </div>

        {needsPosition && (
          <p className="text-[11px] text-neutral-400 pt-1 border-t border-neutral-100">
            Position, VaR and exposure require an active position.
          </p>
        )}
      </div>
    </div>
  );
};

export default PositionRiskWidgetRenderer;
